import { Injectable } from "@angular/core";

import { Completion } from "@codemirror/autocomplete";
import { SQLConfig, SQLDialect, SQLNamespace } from "@codemirror/lang-sql";

import {
    SqlSchema,
    SqlCompletion,
    SqlLanguageSpec,
    SqlLanguageOptions,
} from "../models";
import { SqlEditorLanguageDialectService } from "./sql-editor-language-dialect.service";


@Injectable()
export class SqlEditorLanguageConfigService {

    constructor(private dialectService: SqlEditorLanguageDialectService) {
    }

    public getSqlConfiguration(spec?: SqlLanguageSpec): SQLConfig {
        const options: SqlLanguageOptions = spec?.options ?? {};

        const schema: SQLNamespace = this.getSqlSchemasNamespace(spec?.schemas);

        const dialect: SQLDialect = this.dialectService.getSqlDialect(spec?.dialect, spec?.spec);

        return { schema: schema, dialect: dialect, ...options };
    }

    public getSqlSchemasNamespace(schemas: SqlSchema[]): SQLNamespace {
        if (!schemas?.length) return [];

        // Check if no schema in the list has child elements.
        const leafs: boolean = !schemas.some((schema: SqlSchema): boolean => !!schema.children?.length);

        // If that's the case, the schemas describe a simple namespace. Return basic completions.
        if (leafs) return this.convertToBasicCompletions(schemas);

        // Otherwise, the schemas describe a complex namespace with nesting. Get sql completions.
        const completions: SqlCompletion[] = this.convertToSqlCompletions(schemas);

        // If there is only one completion, return it as namespace.
        if (completions.length == 1) return completions[0];

        // Otherwise, create a namespace collapsing all of them in a record.
        return this.collapseSqlCompletions(completions);
    }

    public convertToBasicCompletions(schemas: SqlSchema[]): Completion[] {
        if (!schemas?.length) return [];

        return schemas
            // Remove schemas that do not have schema name.
            .filter((schema: SqlSchema): boolean => !!schema?.name?.length)

            // Convert each them to completion.
            .map((schema: SqlSchema): Completion => <Completion>{ label: schema.name });
    }

    public convertToSqlCompletions(schemas: SqlSchema[]): SqlCompletion[] {
        if (!schemas?.length) return [];

        return schemas
            // Remove schemas that do not have schema name.
            .filter((schema: SqlSchema): boolean => !!schema?.name?.length)

            // Convert each them to namespace. Recursively resolve its children.
            .map((schema: SqlSchema): SqlCompletion => {

                const completion: Completion = { label: schema.name };
                const namespace: SqlCompletion = { self: completion, children: [] };

                if (!schema.children?.length) return namespace;

                namespace.children = this.getSqlSchemasNamespace(schema.children);

                return namespace;
            });
    }

    public collapseSqlCompletions(completions: SqlCompletion[]): SQLNamespace {
        if (!completions?.length) return {};

        return completions
            // Remove completions that do not have label.
            .filter((completion: SqlCompletion): boolean => !!completion?.self?.label?.length)

            // Create a record of <name, sql-completion> and return it as namespace.
            .reduce((namespace: SQLNamespace, completion: SqlCompletion): SQLNamespace => {

                namespace[completion.self.label] = completion

                return namespace;

            }, {} as SQLNamespace);
    }
}